import { Link } from "react-router-dom";
import { Image } from "@/components/ui/image";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import InAppLink from "@/components/InAppBrowserLink";
import { CARRIERS } from "@/lib/carriers";
import { getCarrierSlug } from "@/lib/carrierSeo";
import CarrierBadge from "@/components/CarrierBadge";
import {
  Package, Zap, TrendingDown, Headphones, Radar, ShoppingCart, BarChart3,
  ShieldCheck, Check, ArrowRight, ChevronDown, FileText, Send, Star, Code, MapPin
} from "lucide-react";
import LandingNav from "@/components/landing/LandingNav";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ModelosFloatWidget from "@/components/landing/ModelosFloatWidget";
import FloatingCartButton from "@/components/store/FloatingCartButton";
import LandingQuote from "@/components/landing/LandingQuote";
import { useState, useEffect, lazy, Suspense } from "react";
import LandingBottomNav from "@/components/landing/LandingBottomNav";
import { Reveal, AuroraBackground, GradientText, Marquee } from "@/components/landing/MotionKit";
import LogisticsHeroMap from "@/components/landing/LogisticsHeroMap";
// Secciones below-the-fold: lazy para reducir el entry chunk (~200KB menos en
// el critical path). En conexiones lentas (1Mbps), el hero + nav + cotizador
// pintan primero; el resto streamea en chunks independientes bajo demanda sin
// bloquear el primer paint. Cada sección tiene su propio Suspense para cargar
// progresivamente (no espera a que todas descarguen).
const LandingFooter = lazy(() => import("@/components/landing/LandingFooter"));
const PaymentMethods = lazy(() => import("@/components/landing/PaymentMethods"));
const ValueAuditSection = lazy(() => import("@/components/landing/ValueAuditSection"));
const EmbedWidgetsSection = lazy(() => import("@/components/landing/EmbedWidgetsSection"));
const BranchFinderSection = lazy(() => import("@/components/landing/BranchFinderSection"));
const AIFeatures = lazy(() => import("@/components/landing/AIFeatures"));
const AIChatQuote = lazy(() => import("@/components/landing/AIChatQuote"));
const RelatedResources = lazy(() => import("@/components/seo/RelatedResources"));
const BusinessModelsSection = lazy(() => import("@/components/landing/BusinessModelsSection"));
const BlogHighlightsSection = lazy(() => import("@/components/landing/BlogHighlightsSection"));
const ReferralBanner = lazy(() => import("@/components/landing/ReferralBanner"));
const PlatformInfrastructureSection = lazy(() => import("@/components/landing/PlatformInfrastructureSection"));
const TrackingSection = lazy(() => import("@/components/landing/TrackingSection"));
const EverywhereSection = lazy(() => import("@/components/landing/EverywhereSection"));
// Los modales se cargan bajo demanda (lazy) para no incluir react-leaflet,
// formularios de cotización y lógica de autenticación en el bundle inicial
// de la landing, mejorando LCP y TTI.
const PackageTrackerModal = lazy(() => import("@/components/landing/PackageTrackerModal"));
const LandingQuoteModal = lazy(() => import("@/components/landing/LandingQuoteModal"));
const LandingAccountModal = lazy(() => import("@/components/landing/LandingAccountModal"));
const AnnouncementModal = lazy(() => import("@/components/AnnouncementModal"));
import { useSeo, faqJsonLd, breadcrumbJsonLd, itemListJsonLd } from "@/hooks/useSeo";
import { usePageSeoImage } from "@/hooks/usePageSeoImage";
// El mapa satelital del hero arrastra react-leaflet+leaflet (~150KB) al bundle
// inicial. Se carga diferido (tras el primer paint) para no bloquear el LCP del
// texto del hero: la tierra aparece con un fade-in poco después, sin coste en
// la carga crítica. Los overlays CSS (LogisticsHeroMap) sí pintan de inmediato.
const SatelliteLogisticsMap = lazy(() => import("@/components/landing/SatelliteLogisticsMap"));

const HERO_IMG = "https://media.base44.com/images/public/6aa6211001d513cafb0a3a5c/7c1a1ce9b_generated_image.png";
const WHATSAPP_LINK = "https://wa.me/529371298761?text=Hola%2C%20me%20interesa%20saber%20m%C3%A1s%20sobre%20BHGC%20Nexus%20Logistics";
const BASE = "https://bhgcnexuslogistics.com";

// ItemList de paqueterías para resultados enriquecidos (carrusel) en Google y Bing.
const CARRIER_ITEM_LIST = itemListJsonLd({
  name: "Paqueterías integradas a BHGC Nexus Logistics",
  url: `${BASE}/comparar-paqueterias`,
  items: CARRIERS.map(c => ({
    name: c.name,
    url: `${BASE}/paqueterias/${getCarrierSlug(c.id)}`,
    description: `Cotizar, generar guías prepagadas y rastrear envíos con ${c.name} en México`,
  })),
});

// Grafo de entidad (Organization + LocalBusiness + WebSite) para SEO local y
// posicionamiento de marca en México: geo de CDMX, área de cobertura nacional
// y catálogo de servicios para resultados enriquecidos y asistentes de voz.
const ORG_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${BASE}/#organization`,
      name: "BHGC Nexus Logistics",
      url: BASE,
      logo: HERO_IMG,
      description: "Plataforma integral de logística en México para cotizar, generar guías prepagadas y rastrear envíos con más de 30 paqueterías.",
      areaServed: { "@type": "Country", name: "México" },
      knowsAbout: ["logística", "paquetería", "envíos", "cotización de envíos", "guías prepagadas", "rastreo de paquetes", "peso volumétrico", "saldo prepago"],
    },
    {
      "@type": "LocalBusiness",
      "@id": `${BASE}/#localbusiness`,
      name: "BHGC Nexus Logistics",
      url: BASE,
      image: HERO_IMG,
      telephone: "+52 937 129 8761",
      priceRange: "$$",
      address: { "@type": "PostalAddress", addressCountry: "MX", addressRegion: "Ciudad de México", addressLocality: "Ciudad de México" },
      geo: { "@type": "GeoCoordinates", latitude: 19.4326, longitude: -99.1332 },
      areaServed: { "@type": "Country", name: "México" },
    },
    {
      "@type": "WebSite",
      "@id": `${BASE}/#website`,
      name: "BHGC Nexus Logistics",
      url: BASE,
      publisher: { "@id": `${BASE}/#organization` },
      inLanguage: "es-MX",
    },
  ],
};

export default function Landing() {
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [trackerQuery, setTrackerQuery] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const openTracker = () => setTrackerOpen(true);
  const openQuote = () => setQuoteOpen(true);
  const openAccount = () => setAccountOpen(true);

  usePageSeoImage("/", {
    title: "BHGC Nexus Logistics — Cotiza, genera guías y rastrea envíos en México",
    description: "Plataforma integral de logística en México para cotizar, generar guías prepagadas y rastrear envíos con DHL, FedEx, Estafeta, Paquetexpress, UPS, J&T Express y más de 30 paqueterías. Compara tarifas en tiempo real, descarga tu etiqueta PDF y gestiona tu saldo prepago.",
    canonical: BASE,
    keywords: "cotizar paquetería México, cotizar envíos, generar guías prepagadas, rastrear paquetes, comparar paqueterías, DHL, FedEx, Estafeta, UPS, Paquetexpress, J&T Express, BHGC Nexus Logistics, logística México, envíos prepago, cotizador multicarrier, peso volumétrico, etiqueta PDF, plugin WooCommerce envíos, integración Shopify, integración TiendaNube, API REST envíos, envíos Ciudad de México, paquetería CDMX, enviar paquete México, cotizador de envíos México, paquetería barata México, rastreo centralizado, saldo prepago, generar guía prepaga México",
    jsonLd: [
      ORG_GRAPH,
      faqJsonLd(LANDING_FAQS),
      breadcrumbJsonLd([{ name: "Inicio", url: BASE }]),
      CARRIER_ITEM_LIST,
    ],
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rastrear = params.get("rastrear");
    if (rastrear) {
      setTrackerQuery(rastrear);
      setTrackerOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Desplazamiento a la sección cuando se llega al landing con un hash (#seccion).
    const hash = window.location.hash?.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, []);

  return (
    <div id="main-content" className="lm-page min-h-screen bg-ink text-ink-text pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
      <LandingNav />
      <Hero onOpenTracker={openTracker} />
      <Suspense fallback={null}><AIChatQuote /></Suspense>
      <Suspense fallback={null}><ReferralBanner /></Suspense>
      <Carriers />
      <Suspense fallback={null}><BranchFinderSection /></Suspense>
      <Suspense fallback={null}><AIFeatures /></Suspense>
      <HowItWorks />
      <WhyUs />
      <Suspense fallback={null}><TrackingSection /></Suspense>
      <Suspense fallback={null}><ValueAuditSection /></Suspense>
      <Suspense fallback={null}><PaymentMethods /></Suspense>
      <Suspense fallback={null}><EmbedWidgetsSection /></Suspense>
      <Integrations />
      <Suspense fallback={null}><PlatformInfrastructureSection /></Suspense>
      <Suspense fallback={null}><EverywhereSection /></Suspense>
      <Pricing />
      <Testimonials />
      <Suspense fallback={null}><BlogHighlightsSection /></Suspense>
      <Suspense fallback={null}><BusinessModelsSection /></Suspense>
      <FAQ />
      <section className="py-14 lm-section-blend">
        <div className="max-w-5xl mx-auto px-4 lg:px-6">
          <h2 className="font-display font-bold text-xl uppercase tracking-wide mb-1">Recursos y guías de envío</h2>
          <p className="text-sm text-ink-muted mb-5">Enlaces útiles para cotizar, comparar y rastrear tus envíos en México.</p>
          <Suspense fallback={null}><RelatedResources current="/" /></Suspense>
        </div>
      </section>
      <ContactCTA />
      <Suspense fallback={null}><LandingFooter onOpenTracker={openTracker} /></Suspense>
      <WhatsAppFloat />
      <FloatingCartButton />
      <ModelosFloatWidget />
      <LandingBottomNav onOpenQuote={openQuote} onOpenTracker={openTracker} onOpenAccount={openAccount} />
      <Suspense fallback={null}>
        <LandingQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
        <PackageTrackerModal open={trackerOpen} onClose={() => { setTrackerOpen(false); setTrackerQuery(""); }} initialQuery={trackerQuery} />
        <LandingAccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />
        <AnnouncementModal />
      </Suspense>
    </div>
  );
}

function SectionHeading({ tag, title, subtitle }) {
  return (
    <Reveal className="text-center max-w-2xl mx-auto mb-12">
      <div className="inline-flex items-center gap-1.5 surface-inset border-cyan/30 px-3 py-1 text-[0.65rem] uppercase tracking-widest text-cyan mb-4">
        {tag}
      </div>
      <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight mb-3">{title}</h2>
      {subtitle && <p className="text-ink-muted text-base leading-relaxed">{subtitle}</p>}
    </Reveal>
  );
}

function Hero({ onOpenTracker }) {
   // El mapa satelital (leaflet) se monta tras el primer paint/idle para no
   // bloquear el LCP. Mientras tanto el hero muestra los overlays CSS sobre el
   // fondo; al llegar el mapa, entra con un fade (sat-stage--ready).
   const { isAuthenticated } = useAuth();
   const [showMap, setShowMap] = useState(false);
  useEffect(() => {
    let done = false;
    const start = () => { if (!done) { done = true; setShowMap(true); } };
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(start, { timeout: 1600 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(start, 350);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="hero-on-map relative pt-16 min-h-[100dvh] flex items-start lg:items-center overflow-hidden">
      {/* En móvil el contenido se apila y el hero crece más allá de 100dvh; si el
          mapa fuera inset-0 el mundo quedaría como una banda en el centro, detrás
          del cotizador. Se confina a la zona superior (cabecera) en móvil y se
          llena todo el hero en desktop. El degradé inferior funde el mapa con el
          fondo sólido de la página debajo del cotizador. */}
      <div className="absolute inset-x-0 top-0 h-[70vh] lg:inset-0 lg:h-full z-0">
        {showMap && (
          <Suspense fallback={null}>
            <SatelliteLogisticsMap />
          </Suspense>
        )}
        <LogisticsHeroMap />
        <div className="hero-map-fade lg:hidden" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-6 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="max-w-2xl min-w-0 text-white">
            <div className="inline-flex items-center gap-2 surface-inset border-cyan/30 px-3 py-1.5 text-xs uppercase tracking-widest text-cyan mb-6">
              <span className="w-1.5 h-1.5 bg-cyan rounded-full animate-pulse" /> Plataforma #1 de logística en México
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
              Envía más. <GradientText>Paga menos.</GradientText><br />
              Siempre con alguien que te responde.
            </h1>
            <p className="hover-bold text-white text-lg leading-relaxed mb-8 max-w-xl cursor-default">
              Compara con {CARRIERS.length}+ paqueterías, genera guías en segundos y recibe atención humana y directa.
              Sin contratos, sin mínimos, sin letras chiquitas.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to={isAuthenticated ? "/cotizar" : "/register"} className="flex items-center gap-2 bg-cyan hover:bg-cyan-hover text-ink px-7 py-3.5 font-display font-bold uppercase tracking-wide glow-cyan transition-colors">
                {isAuthenticated ? "Ir a cotizar" : "Empieza gratis"} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={isAuthenticated ? "/rastrear" : "/login"} className="px-7 py-3.5 font-display font-bold uppercase tracking-wide border border-white/50 text-white hover:border-cyan hover:text-cyan transition-colors">
                {isAuthenticated ? "Ir a rastrear" : "Iniciar sesión"}
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-xs text-white">
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Sin contratos</span>
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Sin mínimos</span>
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Sin letras chiquitas</span>
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Soporte humano por WhatsApp</span>
            </div>

            <div className="mt-8 flex items-center gap-3 text-xs text-white">
              <div className="flex -space-x-2">
                {["#00E5FF", "#4D148C", "#E30613", "#FFCC00"].map(c => (
                  <span key={c} className="w-7 h-7 rounded-full border-2 border-ink" style={{ background: c }} />
                ))}
              </div>
              <span className="hover-bold-white"><strong>+ miles de vendedores</strong> ya ahorran con BHGC Nexus</span>
            </div>
          </div>

          <div className="flex flex-col items-center lg:items-end gap-3 min-w-0 w-full">
            <LandingQuote />
            <button onClick={onOpenTracker}
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan hover:underline">
              <Radar className="w-3.5 h-3.5" /> Rastreador de paquetes
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Carriers() {
  return (
    <section className="py-14 lm-section-blend">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <Marquee speed={45} className="mb-7">
          {CARRIERS.map(c => (
            <span key={c.id} className="flex items-center gap-2 surface-inset px-4 py-2 text-xs font-display font-bold uppercase tracking-wide text-ink-text">
              <span className="w-1.5 h-1.5 bg-cyan rounded-full" /> {c.name}
            </span>
          ))}
        </Marquee>
        <h2 className="text-center text-xs uppercase tracking-widest text-ink-muted mb-7">
          Conectamos con las principales paqueterías de México
        </h2>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-ink-muted mb-8">
          <Link to="/cotizador-con-ia" className="py-1.5 hover:text-cyan transition-colors">Cotizador con IA</Link>
          <Link to="/comparar-paqueterias" className="py-1.5 hover:text-cyan transition-colors">Comparar paqueterías</Link>
          <Link to="/cotizacion-en-tiempo-real" className="py-1.5 hover:text-cyan transition-colors">Cotización en tiempo real</Link>
          <Link to="/rastreo-centralizado" className="py-1.5 hover:text-cyan transition-colors">Rastreo centralizado</Link>
          <Link to="/recoleccion-agendable" className="py-1.5 hover:text-cyan transition-colors">Recolección agendable</Link>
          <Link to="/saldo-prepago" className="py-1.5 hover:text-cyan transition-colors">Saldo prepago</Link>
          <Link to="/zonas-postales" className="py-1.5 hover:text-cyan transition-colors">Zonas postales</Link>
          <Link to="/calculadora-volumetrica" className="py-1.5 hover:text-cyan transition-colors">Calculadora volumétrica</Link>
          <Link to="/recursos" className="py-1.5 hover:text-cyan transition-colors">Guías y recursos</Link>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {CARRIERS.map(c => (
            <Link key={c.id} to={`/paqueterias/${getCarrierSlug(c.id)}`} aria-label={`Cotizar, generar guías y rastrear con ${c.name}`} title={`Cotizar, generar guías y rastrear con ${c.name}`} className="transition-transform hover:scale-105 hover:glow-cyan">
              <CarrierBadge carrier={c.id} />
            </Link>
          ))}
        </div>
        <div className="mt-9 max-w-4xl mx-auto">
          <p className="text-center text-[0.65rem] uppercase tracking-widest text-ink-muted mb-3">Directorio de paqueterías integradas</p>
          <nav className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
            {CARRIERS.map(c => (
              <Link key={c.id} to={`/paqueterias/${getCarrierSlug(c.id)}`} className="text-ink-muted hover:text-cyan transition-colors text-center sm:text-left py-1.5">
                {c.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", icon: Package, title: "Cotiza en segundos", text: `Ingresa origen, destino, peso y dimensiones. Ves los precios de las ${CARRIERS.length}+ paqueterías al instante.` },
    { n: "02", icon: TrendingDown, title: "Elige la mejor opción", text: "Compara por precio, tiempo de entrega o paquetería favorita. Tú decides qué priorizar en cada envío." },
    { n: "03", icon: Send, title: "Genera tu guía", text: "Compra, imprime y solicita recolección a domicilio — o entrega en la sucursal más cercana." }
  ];
  return (
    <section id="como-funciona" className="py-20">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <SectionHeading tag="Cómo funciona" title="Tres pasos. Listo." subtitle="Sin contratos, sin mínimos, sin tutoriales de 20 páginas. Si sabes escribir una dirección, sabes usar BHGC Nexus." />
        <div className="grid md:grid-cols-3 gap-5">
          {steps.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="surface p-7 hover:glow-cyan transition-shadow">
                <div className="flex items-center justify-between mb-5">
                  <span className="font-display font-bold text-3xl text-ink-muted">{s.n}</span>
                  <Icon className="w-7 h-7 text-cyan" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const stats = [
    { v: "+8,000", l: "clientes activos" },
    { v: "98%", l: "satisfacción" },
    { v: "40%", l: "ahorro promedio" },
    { v: "<2 min", l: "para tu primera guía" }
  ];
  const features = [
    { icon: TrendingDown, title: "Ahorro real", text: `Ahorra hasta 40% en cada envío. Tarifas preferenciales negociadas con las ${CARRIERS.length}+ paqueterías más grandes del país — sin contratos ni mínimos.` },
    { icon: Headphones, title: "Ejecutivo humano dedicado", text: "Un asesor real que conoce tu negocio, atiende solicitudes especiales y resuelve por WhatsApp en minutos." },
    { icon: Radar, title: "Rastreo centralizado", text: "Todos tus envíos en una sola pantalla con línea de tiempo, mapa logístico en vivo y sincronización cada 10 min — sin importar la paquetería." },
    { icon: ShoppingCart, title: "Integra tu tienda", text: "WooCommerce, Shopify, TiendaNube, Mercado Libre y API REST para sincronizar órdenes y generar guías automáticamente al vender." },
    { icon: FileText, title: "Facturación automática", text: "Recarga, factura y descarga tus CFDI desde tu panel. Sin pedirlo cada mes." },
    { icon: ShieldCheck, title: "Protección y verificación", text: "Todas las guías incluyen servicio de protección de envíos básico, protección adicional por valor declarado y verificación de identidad para enviar con confianza." }
  ];
  return (
    <section id="beneficios" className="py-20 lm-section-blend">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <SectionHeading tag="Por qué BHGC Nexus" title="Todo lo que tu negocio necesita para enviar con confianza." subtitle="No somos solo otra plataforma. Somos tu aliado logístico — con atención personal y directa." />

        {/* Ahorro destacado */}
        <div className="surface p-8 mb-8 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
          <div>
            <div className="label mb-2">Ahorro real</div>
            <h3 className="font-display font-bold text-2xl mb-3">Ahorra hasta 40% en cada envío.</h3>
            <p className="text-sm text-ink-muted leading-relaxed mb-4">
              Tarifas preferenciales negociadas con las {CARRIERS.length}+ paqueterías más grandes del país. Precios que normalmente son solo para grandes empresas — sin contratos ni mínimos.
            </p>
            <Link to="/register" className="inline-flex items-center gap-1.5 text-cyan text-sm font-medium hover:underline">
              Empieza gratis y compruébalo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="surface-inset p-5 text-center">
              <div className="label mb-2">Precio lista</div>
              <div className="font-display font-bold text-3xl text-ink-muted line-through">$158</div>
            </div>
            <div className="surface-inset p-5 text-center border-cyan/40">
              <div className="label mb-2 text-cyan">Con BHGC</div>
              <div className="font-display font-bold text-3xl text-cyan">$95</div>
            </div>
            <div className="col-span-2 surface-inset p-4 text-center">
              <div className="font-display font-bold text-4xl text-success">40%</div>
              <div className="text-xs text-ink-muted uppercase tracking-wide">ahorro promedio</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {features.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="surface p-6">
                <Icon className="w-6 h-6 text-cyan mb-4" />
                <h3 className="font-display font-bold text-base mb-2">{f.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>

        <Reveal className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map(s => (
            <div key={s.l} className="text-center surface-inset p-5">
              <div className="font-display font-bold text-3xl text-cyan">{s.v}</div>
              <div className="text-xs text-ink-muted uppercase tracking-wide mt-1">{s.l}</div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function Integrations() {
  const items = [
    { t: "Mercado Libre", icon: BarChart3 }, { t: "Carga Masiva CSV", icon: FileText }, { t: "Webhooks", icon: Zap }
  ];
  const points = [
    "Guías automáticas cuando entra una venta",
    "Estados sincronizados en ambas direcciones",
    "Webhooks para eventos personalizados",
    "Carga masiva por CSV o Excel con validación de saldo"
  ];
  const stores = [
    { to: "/plugin-woocommerce", t: "Plugin WooCommerce", d: "Cotiza y genera guías en WordPress con más de 30 paqueterías. Sin mensualidad.", icon: ShoppingCart },
    { to: "/integracion-shopify", t: "Integración Shopify", d: "Tarifas reales en tu checkout y guías con un click desde Shopify.", icon: ShoppingCart },
    { to: "/integracion-tiendanube", t: "Integración TiendaNube", d: "Sincroniza pedidos de TiendaNube y genera guías con las mejores paqueterías.", icon: BarChart3 },
    { to: "/api-rest", t: "API REST de envíos", d: "Una sola API para cotizar, generar guías y rastrear con más de 30 paqueterías de México.", icon: Code },
    { to: "/rastreadores-integrados-api", t: "Rastreadores API + IA", d: "Rastreo unificado de 30+ carriers por API con detección automática del transportista e IA.", icon: Radar },
    { to: "/conexiones-api-maps", t: "Conexiones API Maps", d: "Google Maps, OpenStreetMap y 9 proveedores de geolocalización con fallback automático.", icon: MapPin },
  ];
  return (
    <section id="integraciones" className="py-20">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <SectionHeading tag="Integraciones" title="Tu tienda, conectada en minutos." subtitle="Sincroniza órdenes automáticamente, genera guías con un click y manda notificaciones a tus clientes — sin copiar y pegar nada." />
        <div className="grid md:grid-cols-[1fr_1fr] gap-8 items-center mb-12">
          <div>
            <ul className="space-y-3 mb-6">
              {points.map(p => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-ink-text">
                  <Check className="w-4 h-4 text-success mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Link to="/register" className="inline-flex items-center gap-1.5 text-cyan text-sm font-medium hover:underline">
              Conectar mi tienda <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {items.map(it => {
              const Icon = it.icon;
              return (
                <div key={it.t} className="surface p-5 flex flex-col items-center gap-2 hover:glow-cyan transition-shadow">
                  <Icon className="w-6 h-6 text-cyan" />
                  <span className="text-xs font-medium text-center">{it.t}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {stores.map(s => {
            const Icon = s.icon;
            return (
              <Link key={s.to} to={s.to} className="surface p-6 hover:glow-cyan transition-shadow group">
                <Icon className="w-7 h-7 text-cyan mb-3" />
                <h3 className="font-display font-bold text-base mb-2 group-hover:text-cyan transition-colors">{s.t}</h3>
                <p className="text-xs text-ink-muted leading-relaxed mb-3">{s.d}</p>
                <span className="inline-flex items-center gap-1.5 text-xs text-cyan font-medium">Ver integración <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const no = ["Mensualidad o renta fija", "Mínimo de envíos al mes", "Comisión por integrar tu tienda", "Contrato y permanencia forzosa", "Cobro extra por API o webhooks", "Letras chiquitas escondidas"];
  const yes = ["Pagas $0 hasta que envías", "Sin volumen mínimo", "Integraciones incluidas", "Sin contratos, te quedas si quieres", "API y webhooks sin costo", "Tarifas claras, una por envío"];
  return (
    <section id="precios" className="py-20 lm-section-blend">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <SectionHeading tag="Precios honestos" title="Sin mensualidad. Sin letras chiquitas." subtitle="Otras plataformas cobran renta mensual, mínimos o cuotas escondidas. En BHGC Nexus solo pagas la guía que generas. Si un mes no envías, no pagas." />
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <div className="surface p-7">
            <div className="label mb-4">Lo que no cobramos</div>
            <ul className="space-y-3">
              {no.map(n => (
                <li key={n} className="flex items-center gap-2.5 text-sm text-ink-muted">
                  <span className="w-5 h-5 flex items-center justify-center text-destructive border border-destructive/40 shrink-0">✕</span> {n}
                </li>
              ))}
            </ul>
          </div>
          <div className="surface p-7 border-cyan/40 glow-cyan">
            <div className="label mb-4 text-cyan">Lo que sí hacemos</div>
            <ul className="space-y-3">
              {yes.map(y => (
                <li key={y} className="flex items-center gap-2.5 text-sm text-ink-text">
                  <Check className="w-5 h-5 text-success shrink-0" /> {y}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-center text-sm text-ink-muted mt-6">
          ¿Tienes alto volumen? Tu ejecutivo negocia tarifas mejores contigo.{" "}
          <InAppLink href={WHATSAPP_LINK} className="text-cyan underline">Habla con un asesor →</InAppLink>
        </p>
      </div>
    </section>
  );
}

function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    base44.entities.Testimonial.filter({ active: true }, "order", 6)
      .then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const fallback = [
    { quote: "El ejecutivo me conoce por nombre. Cuando tengo un paquete con problema, una llamada y listo. No más tickets perdidos en el vacío.", name: "Regina Salinas", role: "Fundadora", company: "Casa Tinta" },
    { quote: "Cambié de otra plataforma porque los precios de BHGC Nexus son claramente mejores. En mi primer mes ahorré como $9,000 pesos.", name: "Carlos Medina", role: "Ecommerce Manager", company: "Norte Verde" },
    { quote: "Tenía una solicitud rara — mandar 300 cajas a una feria en Oaxaca en dos días. Me armaron la logística completa. Llegaron todas.", name: "Alejandra Robles", role: "Ops", company: "Café del Istmo" }
  ];
  const list = items.length > 0 ? items : fallback;

  if (!loading && items.length === 0 && fallback.length === 0) return null;

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <SectionHeading tag="Clientes" title="No son reviews. Son relaciones." subtitle="Atender bien no es una promesa de marketing. Es cómo trabajamos todos los días." />
        <div className="grid md:grid-cols-3 gap-5">
          {list.map((t, i) => (
            <div key={t.id || i} className="surface p-7 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="text-cyan text-4xl font-display leading-none">“</div>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className={`w-3.5 h-3.5 ${s < (t.rating || 5) ? "text-cyan fill-cyan" : "text-ink-border"}`} />
                  ))}
                </div>
              </div>
              <p className="text-sm text-ink-text leading-relaxed flex-1">{t.quote}</p>
              <div className="mt-5 pt-4 border-t border-ink-border flex items-center gap-3">
                {t.avatar_url ? (
                  <img src={t.avatar_url} alt={t.name} className="w-10 h-10 object-cover" />
                ) : (
                  <div className="w-10 h-10 bg-ink-border flex items-center justify-center font-display font-bold text-sm">{(t.name || "").slice(0, 1)}</div>
                )}
                <div>
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-ink-muted">{[t.role, t.company].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const LANDING_FAQS = [
  { id: "faq-como-funciona", q: "¿Cómo funciona BHGC Nexus Logistics?", a: "Crea tu cuenta gratis, registra tu envío, elige la paquetería que prefieras, genera la guía y entrega el paquete en el punto de recolección o solicita recolección a domicilio. Todo desde tu panel." },
  { id: "faq-paqueterias", q: "¿Con qué paqueterías trabajan?", a: `Estamos integrados con más de ${CARRIERS.length} paqueterías, entre ellas DHL, FedEx, UPS, Estafeta, Paquetexpress, Redpack, J&T Express, 99 Minutos, Ivoy, iMile y muchas más. Constantemente sumamos opciones nuevas.` },
  { id: "faq-costo", q: "¿Tiene algún costo la plataforma?", a: "No. No cobramos mensualidad ni tarifas ocultas. Solo pagas el costo de la guía que generas. Puedes recargar saldo desde tu billetera con tarjeta, Apple Pay, Google Pay, MercadoPago (tarjeta, SPEI, OXXO, efectivo o Crédito MP), PayPal, Openpay, transferencia SPEI o depósito directo con comprobante." },
  { id: "faq-proteccion", q: "¿Las guías incluyen protección?", a: "Sí, todas las guías incluyen un servicio de protección de envíos básico. Si tu envío vale más, puedes contratar protección adicional por el valor total declarado." },
  { id: "faq-facturar", q: "¿Puedo facturar mis envíos?", a: "Sí. Desde tu cuenta puedes facturar automáticamente tus recargas y descargar las facturas cuando las necesites." },
  { id: "faq-rastreo", q: "¿Cómo rastreo mis envíos?", a: "Todos tus envíos aparecen en tu panel con estado actualizado en tiempo real, línea de tiempo y mapa interactivo. Tus clientes reciben notificaciones automáticas por correo." },
  { id: "faq-retraso", q: "¿Qué pasa si un paquete se retrasa o se pierde?", a: "Tu ejecutivo dedicado gestiona la aclaración con la paquetería directamente. No te dejamos lidiando solo con el soporte de ellos." },
  { id: "faq-tienda", q: "¿Puedo conectar mi tienda en línea?", a: "Sí, tenemos integraciones nativas con WooCommerce, Shopify y TiendaNube, carga masiva por CSV y API REST para integraciones personalizadas. Genera guías automáticamente al recibir un pedido y sincroniza el estatus del envío con tu tienda." }
];

function FAQ() {
  const faqs = LANDING_FAQS;
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-20 lm-section-blend">
      <div className="max-w-3xl mx-auto px-4 lg:px-6">
        <SectionHeading tag="Preguntas frecuentes" title="¿Tienes dudas?" subtitle="Lo más preguntado. Si tu duda no está aquí, escríbenos por WhatsApp y te respondemos en minutos." />
        <div className="space-y-2">
          {faqs.map((f, i) => (
            <div key={i} id={f.id} className="surface scroll-mt-24">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
                <span className="font-medium text-sm">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-ink-muted shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-5 pb-4 text-sm text-ink-muted leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section className="py-20">
      <div className="max-w-5xl mx-auto px-4 lg:px-6">
        <div className="surface p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 data-grid opacity-20" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 surface-inset border-cyan/30 px-3 py-1 text-[0.65rem] uppercase tracking-widest text-cyan mb-5">
              <Headphones className="w-3.5 h-3.5" /> Atención directa por WhatsApp
            </div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight mb-4">
              ¿Listo para <span className="text-cyan">ahorrar</span> en tus envíos?
            </h2>
            <p className="text-ink-muted text-base leading-relaxed max-w-xl mx-auto mb-8">
              Crea tu cuenta gratis y genera tu primera guía en menos de 2 minutos. Un asesor te atiende por WhatsApp cuando lo necesites.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" className="flex items-center gap-2 bg-cyan hover:bg-cyan-hover text-ink px-7 py-3.5 font-display font-bold uppercase tracking-wide glow-cyan transition-colors">
                Crear cuenta gratis <ArrowRight className="w-4 h-4" />
              </Link>
              <InAppLink href={WHATSAPP_LINK} className="flex items-center gap-2 px-7 py-3.5 font-display font-bold uppercase tracking-wide border border-ink-border text-ink-text hover:border-cyan hover:text-cyan transition-colors">
                <Send className="w-4 h-4" /> Hablar por WhatsApp
              </InAppLink>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-7 text-xs text-ink-muted">
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Ahorra hasta 40% desde el primer envío</span>
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Ejecutivo dedicado a tu cuenta</span>
              <span className="hover-bold-white flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" /> Sin contratos ni mínimos</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}